import { ReactNode } from 'react';
import clsx from 'clsx';
import { HiOutlineExclamationTriangle, HiXMark } from 'react-icons/hi2';
import { errorReason, errorStatus, errorTraceId } from '@srm/core/errors';
import TraceJournalLink from '../../atoms/TraceJournalLink';

export interface IDataTableFailureProps {
    /** Что именно не загрузилось — первая строка сообщения. */
    title: string;
    /** Отказ: сообщение сервера показывается под заголовком. */
    error: unknown;
    /** Что делать, если сообщения сервера нет. */
    hint?: string;
    /** «Повторить»; без обработчика кнопки нет. */
    onRetry?: () => void;
    /** Плитка на месте списка (block) или полоса над показанными строками (bar). */
    variant?: 'block' | 'bar';
    /** Крестик у полосы: скрыть сообщение, оставив прежние данные. */
    onDismiss?: () => void;
    children?: ReactNode;
}

const DEFAULT_HINT = 'Проверьте соединение и повторите.';

/** Причина отказа: сообщение сервера (без него — `hint`), код ответа и код обращения — своими элементами */
export const FailureReason = ({ error, hint }: { error: unknown; hint?: string }) => {
    const status = errorStatus(error);
    const traceId = errorTraceId(error);
    return (
        <>
            {errorReason(error) ?? hint ?? DEFAULT_HINT}
            {status ? <span className="dtb__fail-code"> · код {status}</span> : null}
            {traceId ? (
                <>
                    <span className="dtb__fail-code"> · обращение {traceId}</span>{' '}
                    <TraceJournalLink traceId={traceId} />
                </>
            ) : null}
        </>
    );
};

/**
 * Сообщение об отказе загрузки — в языке самой таблицы: та же плитка и красный
 * корешок, что у строки с ошибкой, а не всплывающий алерт поверх списка. Форма
 * зависит от потери: плитка, когда показывать нечего, и полоса над строками,
 * когда данные на экране остались от прошлой удачной загрузки.
 */
const DataTableFailure = ({
    title,
    error,
    hint,
    onRetry,
    variant = 'block',
    onDismiss,
    children,
}: IDataTableFailureProps) => {
    return (
        <div className={clsx('dtb__fail', variant === 'bar' && 'dtb__fail--bar')} role="alert">
            <HiOutlineExclamationTriangle className="dtb__fail-ic" aria-hidden />
            <div className="dtb__fail-body">
                <b className="dtb__fail-title">{title}</b>
                <span className="dtb__fail-text">
                    <FailureReason error={error} hint={hint} />
                </span>
                {children}
            </div>
            {onRetry && (
                <button type="button" className="dtb__fail-retry" onClick={onRetry}>
                    Повторить
                </button>
            )}
            {onDismiss && (
                <button type="button" className="dtb__fail-close" onClick={onDismiss} aria-label="Скрыть сообщение">
                    <HiXMark aria-hidden />
                </button>
            )}
        </div>
    );
};

export default DataTableFailure;
